import { baseApi } from './baseApi'
import type { Factory } from '@/config/factories'

// ===================== factoryApi =====================
/**
 * Factory API — generic cho tất cả factory (Bảo Lộc / Nhơn Trạch / Nguyên
 * Cường / Nông Sản / Pacorini). Mọi endpoint nhận `factoryCode` (= DB
 * column `factory_code`: "baoloc" / "nhontrach" / "nguyencuong" /
 * "nongsan" / "pacorini"). URL backend: `/factories/{factoryCode}/...`.
 */

export type FactoryCell = string | number | boolean | null

/** 1 dòng Area/Location (public.factory_area) — scope theo factory_schema. */
export interface FactoryAreaRow {
  id: number
  full_area_code: string | null
  area_code: string | null
  stock: string | null
  square_m2: number | null
  note: string | null
  created_date: string | null
  updated_date: string | null
  created_by: string | null
  updated_by: string | null
  is_status: number
}

/** Body create/update Area/Location. */
export interface FactoryAreaPayload {
  full_area_code: string
  area_code?: string | null
  stock?: string | null
  square_m2?: number | null
  note?: string | null
}

/** Cross-check mass upload Lot Allocation — verdict per dòng (BE cộng dồn pool). */
export interface LotAllocVerdictError {
  scope: 'lot' | 'grn' | 'stack' | null
  field: 'lot_id' | 'stack_id' | 'bag_allocated' | 'kg_allocated' | null
  remaining?: number
  requested?: number
  message: string
}
export interface LotAllocVerdictSuggestion {
  maxBag: number
  maxKg: number
  scope: string
  message: string
}
export interface LotAllocVerdictRow {
  row: number
  ok: boolean
  errors: LotAllocVerdictError[]
  suggestion?: LotAllocVerdictSuggestion | null
}
export interface LotAllocValidateResult {
  ok: boolean
  rows: LotAllocVerdictRow[]
}

/** 1 DO trong combo Lot Allocation — lưu `do_no`; còn lại là field hiển thị. */
export interface LotAllocationDoOption {
  do_no: string
  sale_contract: string | null
  loading_date: string | null
  do_date: string | null
  from_warehouse: string | null
  warehouse_type: string | null
}

export interface FactoryDateRange {
  startDate: string
  endDate: string
}

export interface FactoryContext {
  title: string
  storeChar: string
  factoryCode: string
  viewPrefix: string
  dateRange: FactoryDateRange
  permissions: {
    canView: boolean
    canEdit: boolean
    canExport: boolean
  }
}

export interface FactoryListResponse {
  items: Array<Record<string, FactoryCell>>
  total: number
  source?: string
}

export interface FactorySummarySources {
  sum: string
  byQuality: string
  byCategory: string
}

export interface FactorySummaryStockMapRow {
  fullAreaCode: string
  areaCode: string | null
  qty: number
}

export interface FactorySummaryWarehouseSplit {
  totalStackAndKv: number
  zoneAWithKv: number
  zoneB: number
  zoneC: number
  noArea: number
  kvSx: number
}

export interface FactorySummaryResponse {
  sumRow: Record<string, FactoryCell> | null
  byQuality: Array<Record<string, FactoryCell>>
  byCategory: Array<Record<string, FactoryCell>>
  summaryMapStock?: FactorySummaryStockMapRow[]
  summaryWarehouseSplit?: FactorySummaryWarehouseSplit | null
  sources: FactorySummarySources
}

export interface FactoryListArgs extends FactoryDateRange {
  factoryCode: string
  tab: string
  dataset?: string
}

export type FactoryRowPayload = Record<string, FactoryCell | undefined>
/** @deprecated typo alias — use `FactoryRowPayload`. */
export type FactoryBRowPayload = FactoryRowPayload

export interface FactoryCrudArgs {
  factoryCode: string
  tab: string
  dataset?: string
  body: FactoryRowPayload
}

export interface FactoryCopyReceiveArgs {
  factoryCode: string
  fromGrnId: string
  toGrnId: string
}
export interface FactoryCopyDeliveryArgs {
  factoryCode: string
  fromId: number
  toGdnId: string
}
export interface FactoryCopyConsumablesArgs {
  factoryCode: string
  fromMrnMdnNo: string
  toMrnMdnNo: string
}
export interface FactoryCopyLotMgtArgs {
  factoryCode: string
  fromLotId: string
  toLotId: string
}
export interface FactoryPrintReceiveArgs {
  factoryCode: string
  grnId: string
}
export interface FactoryPrintDeliveryArgs {
  factoryCode: string
  deliveryId: number
}

function factoryUrl(factoryCode: string, tail: string) {
  return `/factories/${encodeURIComponent(factoryCode)}/${tail}`
}

function factoryCrudUrl(factoryCode: string, tab: string, dataset?: string) {
  const base = factoryUrl(factoryCode, encodeURIComponent(tab))
  if (dataset) return `${base}?dataset=${encodeURIComponent(dataset)}`
  return base
}

/** Cache tag id: factoryCode:tab[:dataset] — invalidation tách theo từng factory. */
function factoryCacheTag(factoryCode: string, tab: string, dataset?: string | null) {
  const base = `${factoryCode}:${tab}`
  return dataset ? `${base}:${dataset}` : base
}

export interface FactoryNavigationPrefs {
  lastFactory: string | null
  tabsByFactory: Record<string, string>
  filtersByFactory: Record<string, { startDate: string; endDate: string }>
  /** factoryCode → tabSegmentKey ("<tab>:<segment>") → visible column field list. */
  columnsByFactoryTab: Record<string, Record<string, string[]>>
}

/** Response của `/factories/{code}/preferences/columns` (GET + PATCH).
 *  `columns` = nhánh `[factoryCode]` của user; `defaults` = nhánh
 *  `[factoryCode]` của `username='default'` row. */
export interface FactoryColumnsPrefsResponse {
  columns: Record<string, string[]>
  defaults: Record<string, string[]>
}

export interface FactoryNavigationPatch {
  lastFactory?: string | null
  factoryCode?: string
  /** Last tab on a factory. Send with factoryCode. */
  tab?: string
  /** Filter date range. Send with factoryCode + both startDate/endDate. */
  startDate?: string
  endDate?: string
  /** Column visibility. Send with factoryCode + tabKey + columns. */
  tabKey?: string
  columns?: string[]
}

export const factoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listFactories: builder.query<Factory[], void>({
      query: () => '/factories',
      providesTags: [{ type: 'Factory', id: 'registry' }],
    }),
    getFactoryNavigationPrefs: builder.query<FactoryNavigationPrefs, void>({
      query: () => '/factories/preferences/navigation',
      providesTags: [{ type: 'Factory', id: 'navigation-prefs' }],
      // Khi user rời module Factory, purge entry ngay. Lý do giống các query
      // khác trong file này: tránh stuck cache pending khiến hydrate dates
      // không bao giờ kick-in → spinner mãi mãi sau khi quay lại Factory.
      keepUnusedDataFor: 0,
    }),
    patchFactoryNavigationPrefs: builder.mutation<FactoryNavigationPrefs, FactoryNavigationPatch>({
      query: (body) => ({
        url: '/factories/preferences/navigation',
        method: 'PATCH',
        body,
      }),
      // Optimistic update: cập nhật cache ngay để UI không nháy chờ response.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          factoryApi.util.updateQueryData('getFactoryNavigationPrefs', undefined, (draft) => {
            if (arg.lastFactory !== undefined) draft.lastFactory = arg.lastFactory
            if (arg.factoryCode && arg.tab) {
              if (!draft.tabsByFactory) draft.tabsByFactory = {}
              draft.tabsByFactory[arg.factoryCode] = arg.tab
            }
            if (arg.factoryCode && arg.startDate && arg.endDate) {
              if (!draft.filtersByFactory) draft.filtersByFactory = {}
              draft.filtersByFactory[arg.factoryCode] = {
                startDate: arg.startDate,
                endDate: arg.endDate,
              }
            }
            if (arg.factoryCode && arg.tabKey && arg.columns) {
              if (!draft.columnsByFactoryTab) draft.columnsByFactoryTab = {}
              if (!draft.columnsByFactoryTab[arg.factoryCode]) {
                draft.columnsByFactoryTab[arg.factoryCode] = {}
              }
              draft.columnsByFactoryTab[arg.factoryCode][arg.tabKey] = [...arg.columns]
            }
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
    /** Column visibility — shared row `page_path='factory'`,
     *  `preference_key='columns'`, value nested theo factory_code.
     *  Response shape: `{ columns: {tab: fields}, defaults: {tab: fields} }`
     *  trong đó `defaults` đến từ `username='default'` row (system defaults). */
    getFactoryColumnsPrefs: builder.query<FactoryColumnsPrefsResponse, string>({
      query: (factoryCode) => factoryUrl(factoryCode, 'preferences/columns'),
      providesTags: (_r, _e, factoryCode) => [
        { type: 'Factory', id: `${factoryCode}:cols-prefs` },
      ],
      keepUnusedDataFor: 0,
    }),
    patchFactoryColumnsPrefs: builder.mutation<
      FactoryColumnsPrefsResponse,
      {
        factoryCode: string
        tabKey: string
        columns: string[]
        /** Optional. Khi gửi → BE mirror vào `username='default'` row → dùng
         *  cho nút "Default columns" trong column picker. */
        defaults?: string[]
      }
    >({
      query: ({ factoryCode, tabKey, columns, defaults }) => ({
        url: factoryUrl(factoryCode, 'preferences/columns'),
        method: 'PATCH',
        body: { tabKey, columns, defaults },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          factoryApi.util.updateQueryData(
            'getFactoryColumnsPrefs',
            arg.factoryCode,
            (draft) => {
              if (!draft.columns) draft.columns = {}
              draft.columns[arg.tabKey] = [...arg.columns]
              if (arg.defaults && arg.defaults.length) {
                if (!draft.defaults) draft.defaults = {}
                draft.defaults[arg.tabKey] = [...arg.defaults]
              }
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
    getFactoryContext: builder.query<FactoryContext, string>({
      query: (factoryCode) => factoryUrl(factoryCode, 'context'),
      providesTags: (_r, _e, factoryCode) => [
        { type: 'Factory', id: `${factoryCode}:context` },
      ],
      // Root cause của "Loading factory data…" stuck khi user rời Factory rồi
      // quay lại: context query đôi khi kẹt status='pending' (request bị
      // abort khi unmount, entry không transition rejected). `switchingFactory`
      // ở `FactoryPage` chờ contextData → spinner mãi. Purge entry ngay khi
      // không có subscriber → remount luôn tạo fetch mới.
      keepUnusedDataFor: 0,
    }),
    getFactorySummary: builder.query<FactorySummaryResponse, string>({
      query: (factoryCode) => factoryUrl(factoryCode, 'summary'),
      providesTags: (_r, _e, factoryCode) => [
        { type: 'Factory', id: `${factoryCode}:summary` },
      ],
      // Khi user rời tab Summary, purge cache entry ngay. Tránh trạng thái
      // hiếm gặp: in-flight request bị abort khi switch tab nhanh, entry kẹt
      // status='pending' → quay lại tab → `isFetching=true` mãi mà không có
      // request mới (vì RTK Query nghĩ vẫn đang fetch).
      keepUnusedDataFor: 0,
    }),
    getFactoryTabRows: builder.query<FactoryListResponse, FactoryListArgs>({
      query: ({ factoryCode, tab, startDate, endDate, dataset }) => {
        const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
        if (dataset) params.set('dataset', dataset)
        return `${factoryUrl(factoryCode, encodeURIComponent(tab))}?${params.toString()}`
      },
      providesTags: (_r, _e, args) => [
        { type: 'Factory', id: factoryCacheTag(args.factoryCode, args.tab, args.dataset) },
      ],
      // Lý do giống Summary: chuyển tab/dataset → purge ngay entry cũ để
      // không bao giờ kẹt cache pending → re-subscribe luôn tạo entry mới
      // và fetch fresh. Trade-off: A→B→A trong vòng <60s sẽ thấy spinner
      // ngắn (mất instant cached view), đổi lại không bao giờ "Loading mãi
      // mãi" cần refresh tay.
      keepUnusedDataFor: 0,
    }),
    saveFactoryDateRange: builder.mutation<
      FactoryContext,
      { factoryCode: string } & FactoryDateRange
    >({
      query: ({ factoryCode, ...body }) => ({
        url: factoryUrl(factoryCode, 'filters/date-range'),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: `${arg.factoryCode}:context` },
      ],
    }),
    createFactoryRow: builder.mutation<
      Record<string, FactoryCell>,
      FactoryCrudArgs
    >({
      query: ({ factoryCode, tab, dataset, body }) => ({
        url: factoryCrudUrl(factoryCode, tab, dataset),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, arg.tab, arg.dataset) },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    updateFactoryRow: builder.mutation<
      Record<string, FactoryCell>,
      FactoryCrudArgs
    >({
      query: ({ factoryCode, tab, dataset, body }) => ({
        url: factoryCrudUrl(factoryCode, tab, dataset),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, arg.tab, arg.dataset) },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    deleteFactoryRow: builder.mutation<void, FactoryCrudArgs>({
      query: ({ factoryCode, tab, dataset, body }) => ({
        url: factoryCrudUrl(factoryCode, tab, dataset),
        method: 'DELETE',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, arg.tab, arg.dataset) },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    copyFactoryReceive: builder.mutation<
      Record<string, FactoryCell>,
      FactoryCopyReceiveArgs
    >({
      query: ({ factoryCode, ...body }) => ({
        url: factoryUrl(factoryCode, 'operations/copy-receive'),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'receive') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    copyFactoryDelivery: builder.mutation<
      Record<string, FactoryCell>,
      FactoryCopyDeliveryArgs
    >({
      query: ({ factoryCode, ...body }) => ({
        url: factoryUrl(factoryCode, 'operations/copy-delivery'),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'delivery') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    copyFactoryConsumables: builder.mutation<
      Record<string, FactoryCell>,
      FactoryCopyConsumablesArgs
    >({
      query: ({ factoryCode, ...body }) => ({
        url: factoryUrl(factoryCode, 'operations/copy-consumables'),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'consumables') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    copyFactoryLotMgt: builder.mutation<
      Record<string, FactoryCell>,
      FactoryCopyLotMgtArgs
    >({
      query: ({ factoryCode, ...body }) => ({
        url: factoryUrl(factoryCode, 'operations/copy-lot-mgt'),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'lot-mgt') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    getFactoryPrintReceive: builder.query<
      Record<string, FactoryCell>,
      FactoryPrintReceiveArgs
    >({
      query: ({ factoryCode, grnId }) =>
        `${factoryUrl(factoryCode, 'operations/print-receive')}?grnId=${encodeURIComponent(grnId)}`,
    }),
    /** HTML preview (rpFactoryGrn) cho Print-On flow — same shape as Sale
     *  Contract Print: `{ title, html, message }`. BE enforce Access guards
     *  (PROCESSING / Missing QC) → 422 với detail là user-facing message. */
    getFactoryPrintReceivePreview: builder.query<
      { title: string; html: string; message?: string },
      FactoryPrintReceiveArgs
    >({
      query: ({ factoryCode, grnId }) =>
        `${factoryUrl(factoryCode, 'operations/print-receive/preview')}?grnId=${encodeURIComponent(grnId)}`,
      keepUnusedDataFor: 0,
    }),
    getFactoryPrintDelivery: builder.query<
      Record<string, FactoryCell>,
      FactoryPrintDeliveryArgs
    >({
      query: ({ factoryCode, deliveryId }) =>
        `${factoryUrl(factoryCode, 'operations/print-delivery')}?deliveryId=${encodeURIComponent(String(deliveryId))}`,
    }),
    /** HTML preview (rpFactoryGdn / view_factory_rp_gdn_detail by gdn_id) cho
     *  Delivery Print flow — same shape as receive preview: `{ title, html,
     *  message }`. BE enforce Access guards → 422 với detail user-facing. */
    getFactoryPrintDeliveryPreview: builder.query<
      { title: string; html: string; message?: string },
      FactoryPrintDeliveryArgs
    >({
      query: ({ factoryCode, deliveryId }) =>
        `${factoryUrl(factoryCode, 'operations/print-delivery/preview')}?deliveryId=${encodeURIComponent(String(deliveryId))}`,
      keepUnusedDataFor: 0,
    }),
    /** GET QC payload + cờ `_qc_exists` cho 1 GRN. Trigger thủ công bằng
     *  lazy hook khi user click Quality — không subscribe sẵn.
     *  `providesTags` để mutation `saveReceiveQuality` invalidate được entry
     *  này: bug repro = user click Quality lần 1 → `_qc_exists=false`, save
     *  QC, click Quality lần 2 với cùng args → RTK trả cache cũ (false) →
     *  FE skip confirm dialog. Pair tag với invalidation phía dưới. */
    getReceiveQuality: builder.query<
      Record<string, FactoryCell> & { _qc_exists?: boolean },
      { factoryCode: string; grnId: string }
    >({
      query: ({ factoryCode, grnId }) =>
        `${factoryUrl(factoryCode, 'operations/receive-quality')}?grnId=${encodeURIComponent(grnId)}`,
      keepUnusedDataFor: 0,
      providesTags: (_r, _e, arg) => [
        { type: 'Factory', id: `${arg.factoryCode}:qc:${arg.grnId}` },
      ],
    }),
    /** PATCH QC fields cho 1 GRN. Invalidate receive list để bảng cập
     *  nhật `qc_name` / `inspection_company` / allowance,… nếu đang hiển
     *  thị. Cũng invalidate summary vì deduction tác động tồn kho.
     *  Invalidate `qc:<grnId>` để Quality button lần sau fetch tươi. */
    saveReceiveQuality: builder.mutation<
      Record<string, FactoryCell>,
      { factoryCode: string; grnId: string; body: Record<string, FactoryCell | undefined> }
    >({
      query: ({ factoryCode, grnId, body }) => ({
        url: `${factoryUrl(factoryCode, 'operations/receive-quality')}?grnId=${encodeURIComponent(grnId)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'receive') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
        { type: 'Factory', id: `${arg.factoryCode}:qc:${arg.grnId}` },
      ],
    }),
    /** Next GRN id cho New/Copy Receive — bám Access `Suggest_IndexName`. */
    getSuggestGrnId: builder.query<{ grn_id: string }, { factoryCode: string }>({
      query: ({ factoryCode }) => factoryUrl(factoryCode, 'operations/suggest-grn-id'),
      keepUnusedDataFor: 0,
    }),
    /** Next GDN id cho New/Copy Delivery — bám Access `Suggest_IndexName(... &
     *  "delivery")`. Same shape as suggest-grn-id. */
    getSuggestGdnId: builder.query<{ gdn_id: string }, { factoryCode: string }>({
      query: ({ factoryCode }) => factoryUrl(factoryCode, 'operations/suggest-gdn-id'),
      keepUnusedDataFor: 0,
    }),
    /** Next MRN/MDN no cho New/Copy Consumables — bám Access
     *  `mrn_or_mdn_AfterUpdate` → `Suggest_IndexName(... & "consumables" &
     *  type)`. Mã phụ thuộc type (MRN/MDN) nên truyền `mrnOrMdn`. Format
     *  ``<TYPE>-<store_char>-<NNNNN>``. */
    getSuggestMrnMdnId: builder.query<
      { mrn_mdn_no: string },
      { factoryCode: string; mrnOrMdn: string }
    >({
      query: ({ factoryCode, mrnOrMdn }) =>
        `${factoryUrl(factoryCode, 'operations/suggest-mrn-mdn-id')}?mrnOrMdn=${encodeURIComponent(mrnOrMdn)}`,
      keepUnusedDataFor: 0,
    }),
    /** DO No / Lot ID / Stack ID combo RowSources cho Delivery dialog (Access
     *  Form_Load): `do_no` ← view_factory_delivery_cb_do (typ + do_no),
     *  `lot_id` ← lot_mgt active (label=lot_name), `stack_id` ← stack active
     *  (label=warehouse). Khi truyền `lotId` → BE trả filtered stack list
     *  (Access `lot_id_AfterUpdate` → view_factory_delivery_cbstackid). */
    getDeliveryCombos: builder.query<
      {
        do_no: Array<{ value: string; label: string; typ: string | null }>
        lot_id: Array<{ value: string; label: string }>
        stack_id: Array<{ value: string; label: string }>
        /** Existing GDNs for the QC "copy quality" combo (gdn_id_copy);
         *  date_out = MAX(date_out) for the dropdown's 3rd column. */
        gdn_copy: Array<{ value: string; label: string; date_out: string | null }>
        /** Factory default from-warehouse (factory_overview.receive_warehouse). */
        receive_warehouse: string
      },
      { factoryCode: string; lotId?: string | null }
    >({
      query: ({ factoryCode, lotId }) => {
        const base = factoryUrl(factoryCode, 'operations/delivery-combos')
        const lid = (lotId ?? '').trim()
        return lid ? `${base}?lotId=${encodeURIComponent(lid)}` : base
      },
      keepUnusedDataFor: 30,
    }),
    /** RowSource combo `category` cho Consumables dialog (Access
     *  `SELECT category, unit FROM FactoryMDQryMaterial` =
     *  tlc_factory.category_material active). label = value = category; `unit`
     *  đi kèm (Access ẩn nhưng FE có thể tham chiếu). */
    getConsumablesCombos: builder.query<
      { category: Array<{ value: string; label: string; unit: string }> },
      { factoryCode: string }
    >({
      query: ({ factoryCode }) => factoryUrl(factoryCode, 'operations/consumables-combos'),
      keepUnusedDataFor: 30,
    }),
    /** Master-data combos DÙNG CHUNG cho form Receive / Stack / PO List
     *  (Access RowSource đọc thẳng bảng master, thay cho distinctValues derive
     *  từ data trên trang). Trả RAW COLUMNS đúng Access SELECT — mỗi form tự
     *  chọn cột value/label + tự sort (vd quality: Receive theo created_date,
     *  PO theo code). warehouses/.../bomCodes shared toàn web; areas + stackIds
     *  scope theo factory. */
    getReferenceCombos: builder.query<
      {
        warehouses: Array<{ warehouse: string }>
        suppliers: Array<{ short_name: string }>
        qualityCodes: Array<{ code: string; description: string | null }>
        packages: Array<{ packaging_code: string; packaging: string | null }>
        inspectionCompanies: Array<{
          service_code: string | null
          service_type: string | null
          short_name: string | null
        }>
        processingTypes: Array<{ po_type: string }>
        bomCodes: Array<{
          bom_code: string
          bom_type: string | null
          quality_input: string | null
          quality_output: string | null
        }>
        areas: Array<{
          full_area_code: string
          area_code: string | null
          note: string | null
          stock: string | null
        }>
        stackIds: Array<{
          stack_id: string
          warehouse: string | null
          is_received_from_po: boolean | null
        }>
        /** Warehouse mặc định + KHÓA của nhà máy (Access DefaultValue =
         *  DLookup receive_warehouse). "" nếu chưa cấu hình. */
        receiveWarehouse: string
      },
      { factoryCode: string }
    >({
      query: ({ factoryCode }) => factoryUrl(factoryCode, 'operations/reference-combos'),
      keepUnusedDataFor: 60,
    }),
    /** Cascade lookup cho Delivery dialog (Access `do_no_AfterUpdate`):
     *  `to_warehouse` + `donet` từ delivery/moving order; `is_move=true` (DO
     *  prefix "MOV") → LOCK to_warehouse + donet. `do_quality`/`stack_quality`
     *  là readonly DLookup display. Field nào không resolve → null. */
    getDeliveryLookups: builder.query<
      {
        to_warehouse: string | null
        donet: number | null
        is_move: boolean
        do_quality: string | null
        stack_quality: string | null
      },
      { factoryCode: string; doNo?: string | null; stackId?: string | null }
    >({
      query: ({ factoryCode, doNo, stackId }) => {
        const params = new URLSearchParams()
        const dn = (doNo ?? '').trim()
        const sid = (stackId ?? '').trim()
        if (dn) params.set('doNo', dn)
        if (sid) params.set('stackId', sid)
        const qs = params.toString()
        return `${factoryUrl(factoryCode, 'operations/delivery-lookups')}${qs ? `?${qs}` : ''}`
      },
      keepUnusedDataFor: 30,
    }),
    /** RowSource combos cho Internal Moving dialog (Access
     *  `frmFactoryMovementQryInternalMovingAdd` Form_Load): stack_id (label =
     *  warehouse), lot_id (label = lot_name), area (current_area + moving_area
     *  dùng chung; value = full_area_code, label "<code> — <stock>"). */
    getInternalMovingCombos: builder.query<
      {
        factory_name: string
        stack_id: Array<{ value: string; label: string }>
        lot_id: Array<{ value: string; label: string }>
        area: Array<{ value: string; label: string }>
      },
      { factoryCode: string }
    >({
      query: ({ factoryCode }) => factoryUrl(factoryCode, 'operations/internal-moving-combos'),
      keepUnusedDataFor: 30,
    }),
    /** Cascade lookup cho Internal Moving dialog (Access `stack_id_AfterUpdate`
     *  / `lot_id_AfterUpdate`): balance_nw/balance_bag/area + quality/package
     *  (stack only). Truyền stackId HOẶC lotId. Field không resolve → null. */
    getInternalMovingLookups: builder.query<
      {
        balance_nw: number | null
        balance_bag: number | null
        area: string | null
        quality: string | null
        package: string | null
      },
      { factoryCode: string; stackId?: string | null; lotId?: string | null }
    >({
      query: ({ factoryCode, stackId, lotId }) => {
        const params = new URLSearchParams()
        const sid = (stackId ?? '').trim()
        const lid = (lotId ?? '').trim()
        if (sid) params.set('stackId', sid)
        if (lid) params.set('lotId', lid)
        const qs = params.toString()
        return `${factoryUrl(factoryCode, 'operations/internal-moving-lookups')}${qs ? `?${qs}` : ''}`
      },
      keepUnusedDataFor: 30,
    }),
    /** GET QC payload cho 1 Delivery (by gdn_id). Mirror getReceiveQuality —
     *  lazy trigger khi user click Quality. */
    getDeliveryQuality: builder.query<
      Record<string, FactoryCell> & { _qc_exists?: boolean },
      { factoryCode: string; gdnId: string }
    >({
      query: ({ factoryCode, gdnId }) =>
        `${factoryUrl(factoryCode, 'operations/delivery-quality')}?gdnId=${encodeURIComponent(gdnId)}`,
      keepUnusedDataFor: 0,
      providesTags: (_r, _e, arg) => [
        { type: 'Factory', id: `${arg.factoryCode}:dqc:${arg.gdnId}` },
      ],
    }),
    /** PATCH QC fields cho 1 Delivery (by gdn_id). Mirror saveReceiveQuality. */
    saveDeliveryQuality: builder.mutation<
      Record<string, FactoryCell>,
      { factoryCode: string; gdnId: string; body: Record<string, FactoryCell | undefined> }
    >({
      query: ({ factoryCode, gdnId, body }) => ({
        url: `${factoryUrl(factoryCode, 'operations/delivery-quality')}?gdnId=${encodeURIComponent(gdnId)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'delivery') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
        { type: 'Factory', id: `${arg.factoryCode}:dqc:${arg.gdnId}` },
      ],
    }),
    /** Next PO No cho New PO List (`PRO-<store_char>-<NNNNN>`). Enhancement
     *  ngoài Access spec (yêu cầu chị Đẹp) — auto-fill + regen khi trùng. */
    getSuggestPoNo: builder.query<{ po_no: string }, { factoryCode: string }>({
      query: ({ factoryCode }) => factoryUrl(factoryCode, 'operations/suggest-po-no'),
      keepUnusedDataFor: 0,
    }),
    /** Next Lot id cho New/Copy Lot Mgt (`Lot-<store_char>-<NNNNN>`) — bám
     *  Access `Suggest_IndexName(... & "lot_mgt")`. */
    getSuggestLotId: builder.query<{ lot_id: string }, { factoryCode: string }>({
      query: ({ factoryCode }) => factoryUrl(factoryCode, 'operations/suggest-lot-id'),
      keepUnusedDataFor: 0,
    }),
    /** Standard/Real lookup cho bean defects — bám Access
     *  `look_parameter_standard` / `look_parameter_real_quality`. */
    getQualityReferences: builder.mutation<
      Record<string, { standard: number | null; real: number | null }>,
      { factoryCode: string; qualityCode: string; fieldValues: Record<string, number> }
    >({
      query: ({ factoryCode, qualityCode, fieldValues }) => ({
        url: factoryUrl(factoryCode, 'operations/quality-references'),
        method: 'POST',
        body: { qualityCode, fieldValues },
      }),
    }),
    /** Stack QC lookup — bám Access `stack_id_AfterUpdate`. */
    getReceiveStackQcLookup: builder.query<
      {
        locked: boolean
        quality_code: string
        area: string
        origin: string
        package: string
      },
      { factoryCode: string; stackId: string }
    >({
      query: ({ factoryCode, stackId }) =>
        `${factoryUrl(factoryCode, 'operations/receive-stack-qc-lookup')}?stackId=${encodeURIComponent(stackId)}`,
      keepUnusedDataFor: 0,
    }),
    /** Order no options theo suppliers — bám Access `suppliers_AfterUpdate`.
     *  Mỗi option kèm `quality` (Quality Code của order) để FE show thêm. */
    getReceiveOrderNoOptions: builder.query<
      Array<{
        order_no: string
        quality: string | null
        processing_type: string | null
        supplier: string | null
      }>,
      { factoryCode: string; suppliers: string }
    >({
      query: ({ factoryCode, suppliers }) =>
        `${factoryUrl(factoryCode, 'operations/receive-order-no-options')}?suppliers=${encodeURIComponent(suppliers)}`,
      keepUnusedDataFor: 0,
    }),
    /** GET stack snapshot cho dialog Issue New/Edit (Access
     *  `stack_id_AfterUpdate`): balance bag/nw + stack quality. `issueId`
     *  optional — truyền vào để cộng lại issued của chính row đó (edit). */
    getIssueStackSnapshot: builder.query<
      { txt_balance_bag: number; txt_balance_nw: number; stack_quality: string | null },
      { factoryCode: string; stackId: string; issueId?: number | null }
    >({
      query: ({ factoryCode, stackId, issueId }) => {
        const q = new URLSearchParams({ stackId })
        if (issueId != null) q.set('issueId', String(issueId))
        return `${factoryUrl(factoryCode, 'operations/issue-stack-snapshot')}?${q.toString()}`
      },
      keepUnusedDataFor: 0,
    }),
    /** Combo RowSource cho dialog Issue New/Edit (Access frmFactoryQryIssueAdd
     *  Form_Load): `po_no` (po WHERE is_finished=false, active) + `stack_id`
     *  (stack, order id desc). */
    getIssueComboOptions: builder.query<
      // po_no kèm po_date (Access RowSource 2 cột); value lưu = po_no, po_date
      // chỉ để hiển thị "<po_no> — <po_date>" trong dropdown.
      { po_nos: { po_no: string; po_date: string | null }[]; stack_ids: string[] },
      { factoryCode: string }
    >({
      query: ({ factoryCode }) =>
        factoryUrl(factoryCode, 'operations/issue-combo-options'),
      keepUnusedDataFor: 0,
    }),
    /** Combo RowSource cho dialog Lot Allocation New/Edit (Access Form_Load):
     *  `lot_id` (lot_mgt active) + `stack_id` (stack unfinished/active). */
    getLotAllocationOptions: builder.query<
      { lot_ids: string[]; stack_ids: string[] },
      { factoryCode: string }
    >({
      query: ({ factoryCode }) =>
        factoryUrl(factoryCode, 'operations/lot-allocation-options'),
      keepUnusedDataFor: 0,
    }),
    /** DO No RowSource cho dialog Lot Allocation New/Edit + mass upload —
     *  `public.delivery_order_scs` (shared) WHERE is_status=0. Lưu `do_no`;
     *  sale_contract/loading_date là field hiển thị để chọn đúng DO. `doNo`
     *  (edit) inject DO đang lưu nếu đã rời list. */
    getLotAllocationDoOptions: builder.query<
      LotAllocationDoOption[],
      { factoryCode: string; doNo?: string | null }
    >({
      query: ({ factoryCode, doNo }) => {
        const base = factoryUrl(factoryCode, 'operations/lot-allocation-do-options')
        return doNo ? `${base}?doNo=${encodeURIComponent(doNo)}` : base
      },
      keepUnusedDataFor: 0,
    }),
    /** `grn_id` RowSource keyed by stack (Access `stack_id_AfterUpdate`). */
    getLotAllocationGrnOptions: builder.query<
      string[],
      { factoryCode: string; stackId: string }
    >({
      query: ({ factoryCode, stackId }) =>
        `${factoryUrl(factoryCode, 'operations/lot-allocation-grn-options')}?stackId=${encodeURIComponent(stackId)}`,
      keepUnusedDataFor: 0,
    }),
    /** Pending Bag/Kg snapshot cho dialog Lot Allocation (Access
     *  `lot_id_AfterUpdate` + `check_number`). `allocationId` (edit) cộng lại
     *  allocated của chính row để user thấy quota gốc. Null cho phía chưa chọn. */
    getLotAllocationSnapshot: builder.query<
      {
        lot_id_bag: number | null
        lot_id_kg: number | null
        grn_id_bag: number | null
        grn_id_kg: number | null
      },
      {
        factoryCode: string
        lotId?: string | null
        stackId?: string | null
        grnId?: string | null
        allocationId?: number | null
      }
    >({
      query: ({ factoryCode, lotId, stackId, grnId, allocationId }) => {
        const q = new URLSearchParams()
        if (lotId) q.set('lotId', lotId)
        if (stackId) q.set('stackId', stackId)
        if (grnId) q.set('grnId', grnId)
        if (allocationId != null) q.set('allocationId', String(allocationId))
        return `${factoryUrl(factoryCode, 'operations/lot-allocation-snapshot')}?${q.toString()}`
      },
      keepUnusedDataFor: 0,
    }),
    /** Mass upload Lot Allocation — POST `{ rows: [...] }` insert 1 transaction.
     *  Invalidate list + summary tag để bảng refetch sau khi upload. */
    bulkCreateLotAllocation: builder.mutation<
      { created: number; ids: number[] },
      { factoryCode: string; rows: Record<string, FactoryCell | undefined>[] }
    >({
      query: ({ factoryCode, rows }) => ({
        url: factoryUrl(factoryCode, 'operations/lot-allocation-mass'),
        method: 'POST',
        body: { rows },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'lot-allocation') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    /** Cross-check dry-run mass upload Lot Allocation — POST `{ rows }`, KHÔNG
     *  insert. Trả verdict per dòng (pending cộng dồn theo lot + grn/stack) để FE
     *  highlight đúng dòng lỗi. Không invalidate (read-only). */
    validateLotAllocationMass: builder.mutation<
      LotAllocValidateResult,
      { factoryCode: string; rows: Record<string, FactoryCell | undefined>[] }
    >({
      query: ({ factoryCode, rows }) => ({
        url: factoryUrl(factoryCode, 'operations/lot-allocation-mass/validate'),
        method: 'POST',
        body: { rows },
      }),
    }),
    /** 5 DLookup readonly cho Receive dialog (stack_area_*, certification,
     *  license_cert_no, packaging). Field nào không truyền input → null. */
    getReceiveLookups: builder.query<
      {
        stack_area_original: string | null
        stack_area_latest: string | null
        certification: string | null
        license_cert_no: string | null
        packaging: string | null
      },
      { factoryCode: string; stackId?: string; orderNo?: string; package?: string }
    >({
      query: ({ factoryCode, stackId, orderNo, package: pkg }) => {
        const params = new URLSearchParams()
        if (stackId) params.set('stackId', stackId)
        if (orderNo) params.set('orderNo', orderNo)
        if (pkg) params.set('package', pkg)
        const qs = params.toString()
        return `${factoryUrl(factoryCode, 'operations/receive-lookups')}${qs ? `?${qs}` : ''}`
      },
      keepUnusedDataFor: 30,
    }),
    /** Contract suggest list cho Lot Mgt dialog (Access contract_no combo +
     *  DLookUp). Mỗi contract kèm quality/client/package → FE auto-fill khi chọn.
     *  Nguồn: public.mv_local_s WHERE finalized_text='N' (shared, không theo factory). */
    getLotMgtContracts: builder.query<
      Array<{
        contract_no: string
        contract_quality: string | null
        contract_client: string | null
        contract_package: string | null
      }>,
      { factoryCode: string; contractNo?: string }
    >({
      // contractNo (Edit): contract đang lưu, có thể đã finalized → BE append để
      // FE resolve quality/client/package readonly (Access DLookUp full table).
      query: ({ factoryCode, contractNo }) => {
        const base = factoryUrl(factoryCode, 'operations/lot-mgt-contracts')
        const cn = (contractNo ?? '').trim()
        return cn ? `${base}?contractNo=${encodeURIComponent(cn)}` : base
      },
      keepUnusedDataFor: 60,
    }),
    /** Pre-save check cho New/Copy: GRN id đã có active row chưa? */
    checkGrnIdExists: builder.query<
      { exists: boolean },
      { factoryCode: string; grnId: string }
    >({
      query: ({ factoryCode, grnId }) =>
        `${factoryUrl(factoryCode, 'operations/check-grn-id')}?grnId=${encodeURIComponent(grnId)}`,
      keepUnusedDataFor: 0,
    }),
    /** Pre-save check cho New/Copy Consumables: mrn_mdn_no đã có active row
     *  chưa (parity Access `mrn_mdn_no_AfterUpdate` DCount)? */
    checkMrnMdnIdExists: builder.query<
      { exists: boolean },
      { factoryCode: string; mrnMdnNo: string }
    >({
      query: ({ factoryCode, mrnMdnNo }) =>
        `${factoryUrl(factoryCode, 'operations/check-mrn-mdn-id')}?mrnMdnNo=${encodeURIComponent(mrnMdnNo)}`,
      keepUnusedDataFor: 0,
    }),
    /** Pre-save check cho New PO List: po_no đã có active row chưa? */
    checkPoNoExists: builder.query<
      { exists: boolean },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) =>
        `${factoryUrl(factoryCode, 'operations/check-po-no')}?poNo=${encodeURIComponent(poNo)}`,
      keepUnusedDataFor: 0,
    }),
    /** Pre-save check cho New/Copy Lot Mgt: lot_id đã có active row chưa? */
    checkLotIdExists: builder.query<
      { exists: boolean },
      { factoryCode: string; lotId: string }
    >({
      query: ({ factoryCode, lotId }) =>
        `${factoryUrl(factoryCode, 'operations/check-lot-id')}?lotId=${encodeURIComponent(lotId)}`,
      keepUnusedDataFor: 0,
    }),
    /** Quality Standard — pivot table list (mirror Access Form_Load
     *  `frmFactoryQryDeductionStandard_baoloc`). 1 row/parameter, columns
     *  động theo `quality_columns` (key=quality_factory, label=code). */
    getQualityStandardList: builder.query<
      {
        items: Array<Record<string, FactoryCell>>
        quality_columns: Array<{ code: string; quality_factory: string }>
        total: number
        source: string
      },
      { factoryCode: string }
    >({
      query: ({ factoryCode }) =>
        factoryUrl(factoryCode, 'operations/quality-standard-list'),
      providesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'quality-standard') },
      ],
      keepUnusedDataFor: 0,
    }),
    /** Deduction Rate — pivot list (1 row per parameter+field_value). */
    getDeductionRateList: builder.query<
      {
        items: Array<Record<string, FactoryCell>>
        quality_columns: Array<{ code: string; quality_factory: string }>
        total: number
        source: string
      },
      { factoryCode: string }
    >({
      query: ({ factoryCode }) =>
        factoryUrl(factoryCode, 'operations/deduction-rate-list'),
      providesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'deduction-rate') },
      ],
      keepUnusedDataFor: 0,
    }),
    /** Deduction Rate — load detail per (parameter, field_value). isNew=true
     *  → template (master quality_codes với value=0/rate=0). */
    getDeductionRateDetail: builder.query<
      {
        factory_code: string
        parameter_code: string
        parameter_name: string | null
        field_name: string | null
        field_value: number
        mc_percent: number | null
        rows: Array<{
          quality_code: string
          quality_value: number | null
          quality_rate: number | null
          quality_factory: string | null
        }>
      },
      { factoryCode: string; parameterCode: string; fieldValue: number; isNew?: boolean }
    >({
      query: ({ factoryCode, parameterCode, fieldValue, isNew }) => {
        const q = new URLSearchParams({
          parameterCode,
          fieldValue: String(fieldValue),
        })
        if (isNew) q.set('isNew', 'true')
        return `${factoryUrl(factoryCode, 'operations/deduction-rate-detail')}?${q.toString()}`
      },
      keepUnusedDataFor: 0,
    }),
    saveDeductionRate: builder.mutation<
      { factory_code: string; parameter_code: string; field_value: number; inserted: number; updated: number },
      {
        factoryCode: string
        parameterCode: string
        fieldValue: number
        body: {
          mc_percent?: number | null
          rows: Array<{
            quality_code: string
            quality_value: number | null
            quality_rate: number | null
          }>
        }
      }
    >({
      query: ({ factoryCode, parameterCode, fieldValue, body }) => ({
        url: `${factoryUrl(factoryCode, 'operations/deduction-rate')}?parameterCode=${encodeURIComponent(parameterCode)}&fieldValue=${encodeURIComponent(String(fieldValue))}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'deduction-rate') },
      ],
    }),
    deleteDeductionRate: builder.mutation<
      { factory_code: string; parameter_code: string; field_value: number; deleted: number },
      { factoryCode: string; parameterCode: string; fieldValue: number }
    >({
      query: ({ factoryCode, parameterCode, fieldValue }) => ({
        url: `${factoryUrl(factoryCode, 'operations/deduction-rate')}?parameterCode=${encodeURIComponent(parameterCode)}&fieldValue=${encodeURIComponent(String(fieldValue))}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'deduction-rate') },
      ],
    }),
    /** Area/Location (public.factory_area) — flat CRUD list scope theo factory
     *  (factory_schema). Master data card Data hub. */
    getFactoryAreaList: builder.query<
      { items: FactoryAreaRow[]; total: number; source: string },
      { factoryCode: string }
    >({
      query: ({ factoryCode }) =>
        factoryUrl(factoryCode, 'operations/area-location-list'),
      providesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'area-location') },
      ],
      keepUnusedDataFor: 0,
    }),
    createFactoryArea: builder.mutation<
      FactoryAreaRow,
      { factoryCode: string; body: FactoryAreaPayload }
    >({
      query: ({ factoryCode, body }) => ({
        url: factoryUrl(factoryCode, 'operations/area-location'),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'area-location') },
      ],
    }),
    updateFactoryArea: builder.mutation<
      FactoryAreaRow,
      { factoryCode: string; areaId: number; body: FactoryAreaPayload }
    >({
      query: ({ factoryCode, areaId, body }) => ({
        url: factoryUrl(factoryCode, `operations/area-location/${encodeURIComponent(String(areaId))}`),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'area-location') },
      ],
    }),
    deleteFactoryArea: builder.mutation<
      { id: number; deleted: number },
      { factoryCode: string; areaId: number }
    >({
      query: ({ factoryCode, areaId }) => ({
        url: factoryUrl(factoryCode, `operations/area-location/${encodeURIComponent(String(areaId))}`),
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'area-location') },
      ],
    }),
    /** Receive GRN picker — mirror Access `grn_id_copy` ComboBox RowSource
     *  (form QC line 11733). Trả 3-col list `{grn_id, suppliers, date_in}`. */
    getReceiveGrnList: builder.query<
      Array<{ grn_id: string; suppliers: string | null; date_in: string | null }>,
      { factoryCode: string; q?: string }
    >({
      query: ({ factoryCode, q }) => {
        const url = factoryUrl(factoryCode, 'operations/receive-grn-list')
        return q ? `${url}?q=${encodeURIComponent(q)}` : url
      },
      keepUnusedDataFor: 60,
    }),
    /** Inspection company options — mirror Access ComboBox RowSource (form
     *  QC line 11738). 3 cols: service_code (bound) | service_type | short_name. */
    getInspectionCompanies: builder.query<
      Array<{ service_code: string; service_type: string | null; short_name: string | null }>,
      { factoryCode: string }
    >({
      query: ({ factoryCode }) =>
        factoryUrl(factoryCode, 'operations/inspection-companies'),
      keepUnusedDataFor: 300,
    }),
    /** Quality Standard — load detail per parameter (mirror Access btnEdit
     *  load via tmp table). Trả meta + list quality_code rows. */
    getQualityStandardParameter: builder.query<
      {
        factory_code: string
        parameter_code: string
        parameter_name: string | null
        field_name: string | null
        rows: Array<{
          quality_code: string
          standard_value: number | null
          quality_factory: string | null
        }>
      },
      { factoryCode: string; parameterCode: string }
    >({
      query: ({ factoryCode, parameterCode }) =>
        `${factoryUrl(factoryCode, 'operations/quality-standard-parameter')}?parameterCode=${encodeURIComponent(parameterCode)}`,
      keepUnusedDataFor: 0,
    }),
    /** Bulk upsert (mirror Access btnSave loop): per quality_code →
     *  UPDATE if exists else INSERT. Invalidate quality-standard list. */
    saveQualityStandardParameter: builder.mutation<
      { factory_code: string; parameter_code: string; inserted: number; updated: number },
      {
        factoryCode: string
        parameterCode: string
        body: { rows: Array<{ quality_code: string; standard_value: number | null }> }
      }
    >({
      query: ({ factoryCode, parameterCode, body }) => ({
        url: `${factoryUrl(factoryCode, 'operations/quality-standard-parameter')}?parameterCode=${encodeURIComponent(parameterCode)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'quality-standard') },
      ],
    }),
    /** Parameter-wide soft-delete (mirror Access btnDelete: NO quality filter). */
    deleteQualityStandardParameter: builder.mutation<
      { factory_code: string; parameter_code: string; deleted: number },
      { factoryCode: string; parameterCode: string }
    >({
      query: ({ factoryCode, parameterCode }) => ({
        url: `${factoryUrl(factoryCode, 'operations/quality-standard-parameter')}?parameterCode=${encodeURIComponent(parameterCode)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'quality-standard') },
      ],
    }),
  }),
})

export const {
  useListFactoriesQuery,
  useGetFactoryNavigationPrefsQuery,
  usePatchFactoryNavigationPrefsMutation,
  useGetFactoryColumnsPrefsQuery,
  usePatchFactoryColumnsPrefsMutation,
  useGetFactoryContextQuery,
  useGetFactorySummaryQuery,
  useGetFactoryTabRowsQuery,
  useSaveFactoryDateRangeMutation,
  useCreateFactoryRowMutation,
  useUpdateFactoryRowMutation,
  useDeleteFactoryRowMutation,
  useCopyFactoryReceiveMutation,
  useCopyFactoryDeliveryMutation,
  useCopyFactoryConsumablesMutation,
  useCopyFactoryLotMgtMutation,
  useLazyGetFactoryPrintReceiveQuery,
  useLazyGetFactoryPrintReceivePreviewQuery,
  useLazyGetFactoryPrintDeliveryQuery,
  useLazyGetFactoryPrintDeliveryPreviewQuery,
  useLazyGetReceiveQualityQuery,
  useSaveReceiveQualityMutation,
  useLazyGetDeliveryQualityQuery,
  useSaveDeliveryQualityMutation,
  useLazyGetSuggestGrnIdQuery,
  useLazyGetSuggestGdnIdQuery,
  useLazyGetSuggestMrnMdnIdQuery,
  useGetConsumablesCombosQuery,
  useGetReferenceCombosQuery,
  useGetDeliveryCombosQuery,
  useGetDeliveryLookupsQuery,
  useLazyGetDeliveryLookupsQuery,
  useGetInternalMovingCombosQuery,
  useLazyGetInternalMovingLookupsQuery,
  useLazyGetSuggestPoNoQuery,
  useLazyGetSuggestLotIdQuery,
  useGetQualityReferencesMutation,
  useLazyGetReceiveStackQcLookupQuery,
  useLazyGetReceiveOrderNoOptionsQuery,
  useLazyGetIssueStackSnapshotQuery,
  useGetIssueComboOptionsQuery,
  useGetLotAllocationOptionsQuery,
  useGetLotAllocationDoOptionsQuery,
  useGetLotAllocationGrnOptionsQuery,
  useLazyGetLotAllocationGrnOptionsQuery,
  useGetLotAllocationSnapshotQuery,
  useLazyGetLotAllocationSnapshotQuery,
  useBulkCreateLotAllocationMutation,
  useValidateLotAllocationMassMutation,
  useGetReceiveLookupsQuery,
  useGetLotMgtContractsQuery,
  useLazyCheckGrnIdExistsQuery,
  useLazyCheckMrnMdnIdExistsQuery,
  useLazyCheckPoNoExistsQuery,
  useLazyCheckLotIdExistsQuery,
  useGetQualityStandardListQuery,
  useGetReceiveGrnListQuery,
  useGetInspectionCompaniesQuery,
  useLazyGetQualityStandardParameterQuery,
  useSaveQualityStandardParameterMutation,
  useDeleteQualityStandardParameterMutation,
  useGetDeductionRateListQuery,
  useLazyGetDeductionRateDetailQuery,
  useSaveDeductionRateMutation,
  useDeleteDeductionRateMutation,
  useGetFactoryAreaListQuery,
  useCreateFactoryAreaMutation,
  useUpdateFactoryAreaMutation,
  useDeleteFactoryAreaMutation,
} = factoryApi

// ===================== factoryBomApi =====================
/**
 * Factory BOM API — dedicated cho `/factories/{factoryCode}/bom`. Tách khỏi
 * generic factoryApi vì BOM có sub-form Input/Output children và atomic
 * save flow (parity Access `frmFactoryMDQryBOM_Add btnSave_Click`).
 *
 * Composite business key: `(bom_code, crop_year)`. Detail/update/delete đều
 * cần `crop_year` query param.
 */

export type BomCell = string | number | boolean | null

export interface BomChildRow {
  quality_code: string
  quality_value: number | null
  quality_type?: 'input' | 'output'
  [extra: string]: BomCell | undefined
}

export interface BomListRow {
  bom_code: string
  bom_type: string | null
  crop_year: string | null
  processing_loss: number | null
  moisture_loss: number | null
  note: string | null
  [extra: string]: BomCell | undefined
}

export interface BomListResponse {
  items: BomListRow[]
  total: number
  source: string
}

export interface BomDetail {
  bomCode: string
  bomType: string | null
  cropYear: string | null
  processingLoss: number | null
  moistureLoss: number | null
  note: string | null
  inputs: BomChildRow[]
  outputs: BomChildRow[]
  extra: Record<string, BomCell>
}

export interface BomQualityRef {
  code: string
  liffe: number | null
  diff: number | null
  fx: number | null
  price_from_g2: number | null
  date_actual: string | null
  price_unit: string | null
}

export interface BomQualityRefItem {
  code: string
  liffe: number | null
  diff: number | null
  premium: number | null
  value: number | null
  fx: number | null
  price: number | null
  price_from_g2: number | null
}

export interface BomQualityRefsResponse {
  items: BomQualityRefItem[]
  total: number
}

export interface BomOptions {
  bomTypes: string[]
  qualityItems: Array<{ code: string; [k: string]: BomCell }>
  cropYears: string[]
  currentCropYear: string | null
  nextCropYear: string | null
}

/** Combo `bom_crop` ở form PO List: crop có recipe cho bom_code + crop suggest theo PO date. */
export interface BomCropOptions {
  cropYears: string[]
  suggested: string | null
}

/** Batch report persistence (DELTA) lưu trên `po`. Xem project-bom-bta1. */
export interface BomEditDelta {
  liffe?: number
  g2_diffs?: number
  /** Recipe Default in/out FROZEN (chỉ có khi Finished) — khoá % In/Out. */
  bom?: Record<string, { in?: number | null; out?: number | null }>
  /** Sparse — chỉ quality code có Edit; in/out là phân số (0.40 = 40%). */
  items?: Record<string, { in?: number | null; out?: number | null }>
}
/** Frozen cost ({cost, unitCosts, fx}) — chỉ có khi Finished (Phương án B). */
export interface CostFrozen {
  cost: Record<string, number> | null
  unitCosts: Array<{
    cost_item_code: string
    unit_price: number | null
    valid_from: string | null
    valid_to: string | null
    in_effect: boolean
  }>
  fx: number | null
}
export interface CostEditDelta {
  /** V/C transport manual (VND/ton) — chưa có nguồn BE. */
  vc_hopper?: number
  vc_warehouse?: number
  /** OUTPUT bag mix (tons) BB vs PP/JB (receive) → volume-weight đơn giá Packing. */
  bb_tons?: number
  ppjb_tons?: number
  /** INPUT bag mix (tons) BB vs PP/JB (issue) → volume-weight đơn giá Hopper. */
  in_bb_tons?: number
  in_ppjb_tons?: number
  /** Tấn theo hình thức nhập (Pallet/Stack/Processing) → weighted đơn giá Intake. */
  pallet_tons?: number
  stack_tons?: number
  proc_tons?: number
  /** Số công tơ điện nhập theo từng PO (kWh). submeter = công tơ phụ trừ ra (điện
   *  không thuộc mẻ); extra_deduct = khấu trừ thêm. kWh = (meter_new−meter_old) −
   *  (submeter_new−submeter_old) − extra_deduct. */
  power?: {
    meter_new?: number
    meter_old?: number
    submeter_new?: number
    submeter_old?: number
    extra_deduct?: number
  }
}
export interface BatchReportPersist {
  bomCrop: string | null
  isFinished: boolean // GỘP: true = Finished (BR chốt = PO đóng), false = Processing
  isBatchStandard: number // 0 = Non-std (chưa quy chuẩn), 1 = Std (quy chuẩn)
  bomEdit: BomEditDelta | null
  costEdit: CostEditDelta | null
  /** Frozen cost — chỉ có khi Finished; null → FE tính live. */
  costFrozen: CostFrozen | null
  /** Frozen standard per-item ({quality_premium, price_vs_g2, fx, mc...}) — khi Finished. */
  standardFrozen: Record<string, Record<string, number | null>> | null
  compareEdit: CompareEditDelta | null
  /** Comparison tab "Compare with" PO No — persist trong bom_edit.compare_with. */
  compareWith: string | null
}
export interface BatchReportSaveBody {
  bomCrop?: string | null
  bomEdit?: BomEditDelta | null
  costEdit?: CostEditDelta | null
  compareEdit?: CompareEditDelta | null
}

/** Delta Comparison tab (③ Narrative) — note/standardized nhập tay theo item.
 *  Persist ở po.compare_edit: {items:{<itemCode>:{note?,standardized?}}}. */
export interface CompareEditDelta {
  items?: Record<string, { note?: string | null; standardized?: string | null }>
}

/** Standardization record (tlc_factory.po_standard) — 4 field nhập tay + audit. */
export interface StandardizationRow {
  id: number
  factoryCode: string | null
  poNo: string | null
  stackBefore: string | null
  grnBefore: string | null
  deductRate: number | null
  stackAfter: string | null
  grnAfter: string | null
  reason: string | null
  /** Computed từ view v_batchreport_standard. */
  stackBeforeItem: string | null
  stackBeforeMt: number | null
  deductValue: number | null
  stackAfterItem: string | null
  /** Computed quality (std threshold + after) — các cột sau Reason. */
  blackStd: number | null
  brokensStd: number | null
  fmStd: number | null
  s18Std: number | null
  s16Std: number | null
  s13Std: number | null
  brownStd: number | null
  blackAfterStd: number | null
  brokensAfterStd: number | null
  fmAfterStd: number | null
  s18AfterStd: number | null
  s16AfterStd: number | null
  s13AfterStd: number | null
  brownAfterStd: number | null
  createdDate: string | null
  createdBy: string | null
  updatedDate: string | null
  updatedBy: string | null
}
export interface StandardizationSaveBody {
  stackBefore?: string | null
  grnBefore?: string | null
  deductRate?: number | null
  stackAfter?: string | null
  grnAfter?: string | null
  reason?: string | null
}

/** 1 dòng tab "Item": distinct item của PO + phase/category (list_and_valo) +
 *  mc/blacks/fm (po.standard_edit). Sort sẵn theo order_by (BE). */
export interface BatchReportItemRow {
  poNo: string | null
  item: string | null
  phase: string | null
  category: string | null
  mc: number | null
  blacks: number | null
  brokens: number | null
  fm: number | null
}

/** 1 dòng block "Other info" (read-only). Standard MC/Blacks/Brokens/FM lấy từ
 *  deduction_standard_detail (factory-level) + quality_premium/price_from_g2/fx. */
export interface PoItemInfoRow {
  poNo: string | null
  item: string | null
  phase: string | null
  description: string | null
  mc: number | null
  blacks: number | null
  brokens: number | null
  brown: number | null
  fm: number | null
  qualityPremium: number | null
  priceVsG2: number | null
  fx: number | null
}

/** 1 dòng bảng Rejects/Byproduct (tab Other info). value frozen khi PO Finished. */
export interface RejectsByproductRow {
  parameterCode: string | null
  fieldName: string | null
  rejectedByproduct: number | null
}

/** (Stack, GRN) của PO (view v_batchreport_receive) — combo Stack + GRN before/after.
 *  Grain = (stackId, grnId): 1 stack có thể nhận từ nhiều GRN. */
export interface PoStack {
  stackId: string
  grnId: string | null
  item: string | null
  area: string | null
  mt: number | null
}

export interface BomSavePayload {
  bom_code: string
  bom_type: string
  crop_year: string
  processing_loss?: number | null
  moisture_loss?: number | null
  note?: string | null
  inputs: Array<{ quality_code: string; quality_value?: number | null }>
  outputs: Array<{ quality_code: string; quality_value?: number | null }>
}

const bomUrl = (factoryCode: string, ...segments: string[]): string =>
  ['/factories', factoryCode, 'bom', ...segments].filter(Boolean).join('/')

/* ---- Batch Report Dashboard (aggregation từ 2 view) --------------------- */
export interface BatchReportDashboardKpis {
  inputNetWeight: number
  outputNetWeight: number
  outTurn: number
  loss: number
  totalValueIn: number
  totalValueOut: number
  valueExtracted: number
  valueExtractedPerMt: number
}
export interface BatchReportDashboard {
  kpis: BatchReportDashboardKpis
  outputCategoryDistribution: Array<{ category: string; mt: number }>
  outputItemsMt: Array<{ item: string; mt: number; category?: string }>
  categoryActualVsBom: Array<{ label: string; actual: number; bom: number; category?: string }>
  itemActualVsBom: Array<{ label: string; actual: number; bom: number; category?: string }>
  qualityDefects: Array<{ label: string; input: number; output: number; diff: number }>
  qualityScreen: Array<{ label: string; input: number; output: number; diff: number }>
  valueInByCategory: Array<{ category: string; value: number }>
  valueOutByCategory: Array<{ category: string; value: number }>
}

/** Production Analysis aggregation (2 view + BOM recipe) — rows shape khớp
 *  BudgetActualRow; loss/extracted khớp LossSummary/ExtractedValueSummary (FE). */
export interface ProductionAnalysisResult {
  inputRows: Array<Record<string, unknown>>
  outputRows: Array<Record<string, unknown>>
  loss: Record<string, number>
  extracted: Record<string, number>
  totalInputMt: number
  /** Source = distinct origin_source ở INPUT (BE join ' | '). */
  source: string
}

/** Comparison aggregation (sheet `Comparison`) — current PO vs comparison PO.
 *  Số liệu tính live từ 2 view + reuse Production Analysis (xem
 *  plan/production/BATCH_REPORT_COMPARISON_DATA_SPEC.md). note/standardized là
 *  free-text analyst (chưa wire store → rỗng). previous/current/variance là fraction. */
export interface YieldComparisonRow {
  item: string
  category: string
  premium: number
  previous: number
  current: number
  variance: number
}
export interface FaqQualityRow {
  po: string
  mc: number; blacks: number; brokens: number; bb: number; fm: number
  mouldy: number; brown: number; partlyBlack: number; immature: number
  s18: number; s16: number; s13: number
}
export interface ComparisonNarrativeRow {
  item: string
  /** Category (phase) của item — tag hiển thị (Graded/By-products/Rejects/FAQ/…). */
  category: string
  mc: number; bb: number; fm: number; s18: number; s16: number; s13: number
  note: string
  standardized: string
}
export interface ComparisonPriceInfo {
  liffe: number | null
  g2Diffs: number
  diffLabel: string
  priceDate: string | null
  premiumByItem: Array<{ item: string; premium: number }>
}
export interface ComparisonProductionInfo {
  totalInput: number
  totalOutturnMt: number
  loss: number
  mcLoss: number
  unexplainedLoss: number
}
/** PnL (Extracted value) — shape khớp get_production_analysis["extracted"]. */
export interface ComparisonPnlInfo {
  valueIn: number
  valueOut: number
  valueExtracted: number
  valueExtractedPerMt: number
  handling: number
  valueExtractedPerMtAfterCost: number
  actualExtracted: number
  bomExtracted: number
  pnlPerMt: number
  totalPnl: number
}
export interface BatchReportComparison {
  currentPoNo: string
  comparisonPoNo: string
  yieldComparison: YieldComparisonRow[]
  faqQuality: FaqQualityRow[]
  narrative: ComparisonNarrativeRow[]
  priceInfo: ComparisonPriceInfo
  productionInfo: ComparisonProductionInfo
  /** Price/Production của comparison PO — block ④/⑤ so sánh cả 2 PO. */
  priceInfoCompare: ComparisonPriceInfo
  productionInfoCompare: ComparisonProductionInfo
  /** PnL (Extracted value) — current + comparison PO (block ⑥). */
  pnlInfo: ComparisonPnlInfo
  pnlInfoCompare: ComparisonPnlInfo
}

export const factoryBomApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFactoryBomList: builder.query<
      BomListResponse,
      { factoryCode: string; cropYear?: string }
    >({
      query: ({ factoryCode, cropYear }) => ({
        url: bomUrl(factoryCode),
        params: cropYear ? { crop_year: cropYear } : undefined,
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:${arg.cropYear ?? 'all'}` },
      ],
    }),

    getFactoryBomOptions: builder.query<BomOptions, string>({
      query: (factoryCode) => bomUrl(factoryCode, 'options'),
      providesTags: (_r, _e, factoryCode) => [
        { type: 'FactoryBom', id: `${factoryCode}:options` },
      ],
    }),

    getBomQualityRef: builder.query<
      BomQualityRef,
      { factoryCode: string; code: string; date?: string }
    >({
      query: ({ factoryCode, code, date }) => ({
        url: bomUrl(factoryCode, 'quality-ref'),
        params: date ? { code, date } : { code },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:qref:${arg.code}:${arg.date ?? 'now'}` },
      ],
    }),

    getBomCropOptions: builder.query<
      BomCropOptions,
      { factoryCode: string; bomCode: string; date?: string }
    >({
      query: ({ factoryCode, bomCode, date }) => ({
        url: bomUrl(factoryCode, 'crop-options'),
        params: date ? { bom_code: bomCode, date } : { bom_code: bomCode },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:cropopt:${arg.bomCode}:${arg.date ?? 'nodate'}` },
      ],
    }),

    getBomQualityRefs: builder.query<
      BomQualityRefsResponse,
      { factoryCode: string; date?: string }
    >({
      query: ({ factoryCode, date }) => ({
        url: bomUrl(factoryCode, 'quality-refs'),
        params: date ? { date } : undefined,
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:qrefs:${arg.date ?? 'now'}` },
      ],
    }),

    getBagMix: builder.query<
      {
        bb_tons: number
        ppjb_tons: number
        in_bb_tons: number
        in_ppjb_tons: number
        pallet_tons: number
        stack_tons: number
        proc_tons: number
      },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'bag-mix'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:bagmix:${arg.poNo}` },
      ],
    }),

    getBatchReportPersist: builder.query<
      BatchReportPersist,
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo)].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:br:${arg.poNo}` },
      ],
    }),

    saveBatchReport: builder.mutation<
      BatchReportPersist,
      { factoryCode: string; poNo: string; body: BatchReportSaveBody }
    >({
      query: ({ factoryCode, poNo, body }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo)].join('/'),
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:br:${arg.poNo}` },
      ],
    }),

    // Đổi cờ trạng thái: isFinished true = Finished (PO đóng), false = Processing.
    setBatchReportStatus: builder.mutation<
      BatchReportPersist,
      { factoryCode: string; poNo: string; isFinished: boolean; poClossingDate?: string | null }
    >({
      query: ({ factoryCode, poNo, isFinished, poClossingDate }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'status'].join('/'),
        method: 'PUT',
        body: { isFinished, poClossingDate: poClossingDate ?? null },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:br:${arg.poNo}` },
        // PO List hiển thị PO → refresh để trạng thái mới phản ánh ngay.
        { type: 'Factory', id: `${arg.factoryCode}:po-list` },
      ],
    }),

    // Đổi cờ quy chuẩn: isBatchStandard 1 = Std (quy chuẩn), 0 = Non-std (chưa).
    setBatchReportStandard: builder.mutation<
      BatchReportPersist,
      { factoryCode: string; poNo: string; isBatchStandard: 0 | 1 }
    >({
      query: ({ factoryCode, poNo, isBatchStandard }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'standard'].join('/'),
        method: 'PUT',
        body: { isBatchStandard },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:br:${arg.poNo}` },
        { type: 'Factory', id: `${arg.factoryCode}:po-list` },
      ],
    }),

    // Comparison tab — lưu "Compare with" PO vào bom_edit.compare_with (merge,
    // không đụng delta BOM). Invalidate persist tag → GET load lại value đúng.
    setBatchReportCompareWith: builder.mutation<
      BatchReportPersist,
      { factoryCode: string; poNo: string; compareWith: string }
    >({
      query: ({ factoryCode, poNo, compareWith }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'compare-with'].join('/'),
        method: 'PUT',
        body: { compareWith },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:br:${arg.poNo}` },
      ],
    }),

    getBatchReportInput: builder.query<
      { items: Array<Record<string, unknown>> },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'input'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:brinput:${arg.poNo}` },
      ],
    }),

    getBatchReportOutput: builder.query<
      { items: Array<Record<string, unknown>> },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'output'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:broutput:${arg.poNo}` },
      ],
    }),

    getBatchReportDashboard: builder.query<
      BatchReportDashboard,
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'dashboard'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:brdash:${arg.poNo}` },
      ],
    }),

    getProductionAnalysis: builder.query<
      ProductionAnalysisResult,
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'production-analysis'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:brpa:${arg.poNo}` },
      ],
    }),

    getBatchReportComparison: builder.query<
      BatchReportComparison,
      { factoryCode: string; poNo: string; comparisonPoNo: string }
    >({
      query: ({ factoryCode, poNo, comparisonPoNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'comparison'].join('/'),
        params: comparisonPoNo ? { comparison_po_no: comparisonPoNo } : undefined,
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:brcmp:${arg.poNo}:${arg.comparisonPoNo}` },
      ],
    }),

    getPoStacks: builder.query<
      { items: PoStack[] },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'stacks'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:stacks:${arg.poNo}` },
      ],
    }),

    // Tab "Item": distinct item (issue ∪ receive) + list_and_valo + po.standard_edit.
    getBatchReportItems: builder.query<
      { items: BatchReportItemRow[] },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'items'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:items:${arg.poNo}` },
      ],
    }),
    // Block "Other info" (read-only): is_finished=false → live (deduction standard
    // factory-level + premium/price/fx); is_finished=true → đọc frozen po.standard_edit.
    // rejectsByproduct cùng quy tắc (frozen value khi Finished).
    getBatchReportItemInfo: builder.query<
      { items: PoItemInfoRow[]; rejectsByproduct: RejectsByproductRow[] },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'item-info'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:item-info:${arg.poNo}` },
      ],
    }),
    // Edit 1 field Standard (mc/blacks/fm) của 1 item → po.standard_edit[item][field].
    saveBatchReportItemStandard: builder.mutation<
      Partial<BatchReportItemRow>,
      { factoryCode: string; poNo: string; item: string; field: 'mc' | 'blacks' | 'brokens' | 'fm'; value: number | null }
    >({
      query: ({ factoryCode, poNo, item, field, value }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'items', 'standard'].join('/'),
        method: 'PUT',
        body: { item, field, value },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:items:${arg.poNo}` },
      ],
    }),

    // ---- po_standard CRUD (Standardization records) ----
    getStandardizationRows: builder.query<
      { items: StandardizationRow[] },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'standardization'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:std:${arg.poNo}` },
      ],
    }),
    createStandardization: builder.mutation<
      StandardizationRow,
      { factoryCode: string; poNo: string; body: StandardizationSaveBody }
    >({
      query: ({ factoryCode, poNo, body }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'standardization'].join('/'),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:std:${arg.poNo}` },
      ],
    }),
    updateStandardization: builder.mutation<
      StandardizationRow,
      { factoryCode: string; poNo: string; id: number; body: StandardizationSaveBody }
    >({
      query: ({ factoryCode, poNo, id, body }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'standardization', String(id)].join('/'),
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:std:${arg.poNo}` },
      ],
    }),
    deleteStandardization: builder.mutation<
      { id: number; deleted: boolean },
      { factoryCode: string; poNo: string; id: number }
    >({
      query: ({ factoryCode, poNo, id }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'standardization', String(id)].join('/'),
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:std:${arg.poNo}` },
      ],
    }),

    getFactoryBomDetail: builder.query<
      BomDetail,
      { factoryCode: string; bomCode: string; cropYear: string }
    >({
      query: ({ factoryCode, bomCode, cropYear }) => ({
        url: bomUrl(factoryCode, encodeURIComponent(bomCode)),
        params: { crop_year: cropYear },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:${arg.cropYear}:${arg.bomCode}` },
      ],
    }),

    createFactoryBom: builder.mutation<
      BomDetail,
      { factoryCode: string; body: BomSavePayload }
    >({
      query: ({ factoryCode, body }) => ({
        url: bomUrl(factoryCode),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:all` },
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:${arg.body.crop_year}` },
      ],
    }),

    updateFactoryBom: builder.mutation<
      BomDetail,
      { factoryCode: string; bomCode: string; cropYear: string; body: BomSavePayload }
    >({
      query: ({ factoryCode, bomCode, cropYear, body }) => ({
        url: bomUrl(factoryCode, encodeURIComponent(bomCode)),
        method: 'PUT',
        params: { crop_year: cropYear },
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:all` },
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:${arg.cropYear}` },
        { type: 'FactoryBom', id: `${arg.factoryCode}:${arg.cropYear}:${arg.bomCode}` },
      ],
    }),

    deleteFactoryBom: builder.mutation<
      void,
      { factoryCode: string; bomCode: string; cropYear: string }
    >({
      query: ({ factoryCode, bomCode, cropYear }) => ({
        url: bomUrl(factoryCode, encodeURIComponent(bomCode)),
        method: 'DELETE',
        params: { crop_year: cropYear },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:all` },
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:${arg.cropYear}` },
        { type: 'FactoryBom', id: `${arg.factoryCode}:${arg.cropYear}:${arg.bomCode}` },
      ],
    }),
  }),
})

export const {
  useGetFactoryBomListQuery,
  useGetFactoryBomOptionsQuery,
  useGetBomCropOptionsQuery,
  useGetBagMixQuery,
  useGetBatchReportPersistQuery,
  useSaveBatchReportMutation,
  useSetBatchReportStatusMutation,
  useSetBatchReportStandardMutation,
  useSetBatchReportCompareWithMutation,
  useGetBatchReportInputQuery,
  useGetBatchReportOutputQuery,
  useGetBatchReportDashboardQuery,
  useGetProductionAnalysisQuery,
  useGetBatchReportComparisonQuery,
  useGetPoStacksQuery,
  useGetBatchReportItemsQuery,
  useLazyGetBatchReportItemsQuery,
  useGetBatchReportItemInfoQuery,
  useSaveBatchReportItemStandardMutation,
  useGetStandardizationRowsQuery,
  useCreateStandardizationMutation,
  useUpdateStandardizationMutation,
  useDeleteStandardizationMutation,
  useGetFactoryBomDetailQuery,
  useGetBomQualityRefQuery,
  useGetBomQualityRefsQuery,
  useCreateFactoryBomMutation,
  useUpdateFactoryBomMutation,
  useDeleteFactoryBomMutation,
} = factoryBomApi

// ===================== factoryCostApi =====================
/**
 * Factory BOM Cost API — dedicated cho `/factories/{factoryCode}/bom-cost`.
 *
 * Mirror pattern factoryBomApi: list / options / detail / create / update / delete.
 * Master cross-factory (bảng `cost_item` không có column factory_code) — `factoryCode`
 * chỉ dùng auth scope.
 */

export type CostCell = string | number | boolean | null

export interface CostListRow {
  cost_item_id: number
  cost_item_code: string
  display_code: string | null
  cost_group_code: string
  cost_group_name_vi: string | null
  cost_group_name_en: string | null
  cost_item_name_vi: string
  cost_item_name_en: string | null
  unit_price: number | null
  currency: string | null
  price_unit: string | null
  valid_from: string | null
  valid_to: string | null
  vendor_name: string | null
  vendor_contract_no: string | null
  vendor_note: string | null
  vendor_log: string | null
  calc_method: string | null
  description: string | null
  cost_group_sort_order: number | null
  cost_item_sort_order: number | null
  status_name: string | null
  updated_date: string | null
  updated_by: string | null
  [extra: string]: CostCell | undefined
}

export interface CostListResponse {
  items: CostListRow[]
  total: number
  source: string
}

export type CostDetail = CostListRow

export interface CostGroupOption {
  cost_group_code: string
  cost_group_name_vi: string | null
  cost_group_name_en: string | null
  sort_order: number | null
}

export interface CostOptions {
  costGroups: CostGroupOption[]
  calcMethods: string[]
}

export interface CostSavePayload {
  cost_item_code: string
  display_code?: string | null
  cost_group_code: string
  cost_item_name_vi: string
  cost_item_name_en?: string | null
  unit_price?: number | null
  currency?: string | null
  price_unit?: string | null
  valid_from?: string | null
  valid_to?: string | null
  vendor_name?: string | null
  vendor_contract_no?: string | null
  vendor_note?: string | null
  calc_method: string
  sort_order?: number | null
  description?: string | null
}

const costUrl = (factoryCode: string, ...segments: string[]): string =>
  ['/factories', factoryCode, 'bom-cost', ...segments].filter(Boolean).join('/')

export const factoryCostApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFactoryCostList: builder.query<
      CostListResponse,
      { factoryCode: string; costGroupCode?: string }
    >({
      query: ({ factoryCode, costGroupCode }) => ({
        url: costUrl(factoryCode),
        params: costGroupCode ? { cost_group_code: costGroupCode } : undefined,
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:${arg.costGroupCode ?? 'all'}` },
      ],
    }),

    getFactoryCostOptions: builder.query<CostOptions, string>({
      query: (factoryCode) => costUrl(factoryCode, 'options'),
      providesTags: (_r, _e, factoryCode) => [
        { type: 'FactoryCost', id: `${factoryCode}:options` },
      ],
    }),

    getFactoryCostDetail: builder.query<
      CostDetail,
      { factoryCode: string; costItemId: number }
    >({
      query: ({ factoryCode, costItemId }) => ({
        url: costUrl(factoryCode, String(costItemId)),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryCost', id: `${arg.factoryCode}:${arg.costItemId}` },
      ],
    }),

    createFactoryCost: builder.mutation<
      CostDetail,
      { factoryCode: string; body: CostSavePayload }
    >({
      query: ({ factoryCode, body }) => ({
        url: costUrl(factoryCode),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:all` },
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:${arg.body.cost_group_code}` },
      ],
    }),

    updateFactoryCost: builder.mutation<
      CostDetail,
      { factoryCode: string; costItemId: number; body: CostSavePayload }
    >({
      query: ({ factoryCode, costItemId, body }) => ({
        url: costUrl(factoryCode, String(costItemId)),
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:all` },
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:${arg.body.cost_group_code}` },
        { type: 'FactoryCost', id: `${arg.factoryCode}:${arg.costItemId}` },
      ],
    }),

    deleteFactoryCost: builder.mutation<
      void,
      { factoryCode: string; costItemId: number; costGroupCode?: string }
    >({
      query: ({ factoryCode, costItemId }) => ({
        url: costUrl(factoryCode, String(costItemId)),
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:all` },
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:${arg.costGroupCode ?? 'all'}` },
        { type: 'FactoryCost', id: `${arg.factoryCode}:${arg.costItemId}` },
      ],
    }),
  }),
})

export const {
  useGetFactoryCostListQuery,
  useGetFactoryCostOptionsQuery,
  useGetFactoryCostDetailQuery,
  useCreateFactoryCostMutation,
  useUpdateFactoryCostMutation,
  useDeleteFactoryCostMutation,
} = factoryCostApi
